import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ChevronDown,
  Clock3,
  IndianRupee,
  Loader2,
  MapPin,
  RefreshCw,
  Star,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import NoImg from "../Images/Item_Image_Placeholder.webp";
import LoadingScreen from "../Utils/LoadingScreen";

import { useRestaurant } from "../Services/useRestaurant";
import { useCatalogueStatus } from "../Services/useCatalogueStatus";
import { useRestaurantDetails } from "../Services/useRestaurantDetails";
import { useGlobalContext } from "../Context/GlobalContext";

const ShimmerStyles = () => (
  <style>
    {`
      @keyframes chaafoShimmerMove {
        0% {
          transform: translateX(-120%);
        }

        100% {
          transform: translateX(120%);
        }
      }

      .chaafo-shimmer {
        position: relative;
        overflow: hidden;
        background: #e5e7eb;
      }

      .chaafo-shimmer::after {
        content: "";
        position: absolute;
        inset: 0;

        transform: translateX(-120%);

        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.35) 30%,
          rgba(255, 255, 255, 0.95) 50%,
          rgba(255, 255, 255, 0.35) 70%,
          transparent 100%
        );

        animation: chaafoShimmerMove 1.25s linear infinite;
        will-change: transform;
        pointer-events: none;
      }
    `}
  </style>
);

const API_LIMIT = 12;
const ALL_RESTAURANT_RANGE_KM = 20;

const filters = [
  {
    id: "all",
    label: "All",
    servingType: "",
  },
  {
    id: "top-rated",
    label: "Top Rated",
    servingType: "",
  },
  {
    id: "under-30",
    label: "Under 30 min",
    servingType: "",
  },
  // {
  //   id: "under-45",
  //   label: "Under 45 min",
  //   servingType: "",
  // },
  // {
  //   id: "under-60",
  //   label: "Under 60 min",
  //   servingType: "",
  // },
  // {
  //   id: "below-50",
  //   label: "Below ₹50",
  //   servingType: "",
  // },
  // {
  //   id: "below-100",
  //   label: "Below ₹100",
  //   servingType: "",
  // },
  // {
  //   id: "below-150",
  //   label: "Below ₹150",
  //   servingType: "",
  // },
  {
    id: "150-300",
    label: "₹150 - ₹300",
    servingType: "",
  },
  {
    id: "above-300",
    label: "Above ₹300",
    servingType: "",
  },
  {
    id: "pure-veg",
    label: "Veg",
    servingType: "V",
  },
  {
    id: "non-veg",
    label: "Non Veg",
    servingType: "N",
  },
  // {
  //   id: "both",
  //   label: "Veg & Non Veg",
  //   servingType: "B",
  // },
];

const getLocationErrorMessage = (error) => {
  if (!error) {
    return "Unable to access your current location.";
  }

  if (error.code === 1) {
    return "Location permission was denied. Please allow location access from browser settings.";
  }

  if (error.code === 2) {
    return "Your current location is unavailable.";
  }

  if (error.code === 3) {
    return "Location request timed out. Please try again.";
  }

  return error.message || "Unable to access your current location.";
};

const getRestaurantServingType = (restaurant) => {
  return String(restaurant?.servingType || restaurant?.serving_type || "")
    .trim()
    .toUpperCase();
};

const getServingLabel = (servingType) => {
  if (servingType === "V") {
    return "Pure Veg";
  }

  if (servingType === "N") {
    return "Non Veg";
  }

  if (servingType === "B") {
    return "Veg & Non Veg";
  }

  return "Restaurant";
};

const getRestaurantUserId = (restaurant) => {
  return String(
    restaurant?.userId ||
      restaurant?.userid ||
      restaurant?.user_id ||
      restaurant?.restaurantUserId ||
      restaurant?.code ||
      "",
  ).trim();
};

const calculateDistance = (
  customerLatitude,
  customerLongitude,
  restaurantLatitude,
  restaurantLongitude,
) => {
  const customerLat = Number(customerLatitude);

  const customerLng = Number(customerLongitude);

  const restaurantLat = Number(restaurantLatitude);

  const restaurantLng = Number(restaurantLongitude);

  if (
    !Number.isFinite(customerLat) ||
    !Number.isFinite(customerLng) ||
    !Number.isFinite(restaurantLat) ||
    !Number.isFinite(restaurantLng)
  ) {
    return null;
  }

  const earthRadius = 6371;

  const toRadians = (value) => (value * Math.PI) / 180;

  const latitudeDifference = toRadians(restaurantLat - customerLat);

  const longitudeDifference = toRadians(restaurantLng - customerLng);

  const firstLatitude = toRadians(customerLat);

  const secondLatitude = toRadians(restaurantLat);

  const value =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(firstLatitude) *
      Math.cos(secondLatitude) *
      Math.sin(longitudeDifference / 2) ** 2;

  const centralAngle = 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));

  return earthRadius * centralAngle;
};

function useThrottledCallback(callback, delay = 1200) {
  const lastExecutedRef = useRef(0);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    (...args) => {
      const currentTime = Date.now();

      if (currentTime - lastExecutedRef.current < delay) {
        return;
      }

      lastExecutedRef.current = currentTime;

      callbackRef.current(...args);
    },
    [delay],
  );
}

const FoodTypeIcon = ({ type }) => {
  const isVeg = type === "V";
  const isNonVeg = type === "N";

  if (!isVeg && !isNonVeg) {
    return null;
  }

  const color = isVeg ? "#16a34a" : "#b91c1c";

  return (
    <div
      title={isVeg ? "Pure Veg" : "Non Veg"}
      className="
        flex
        h-5
        w-5
        items-center
        justify-center
        border
        bg-white
        shadow-sm
      "
      style={{
        borderColor: color,
        borderRadius: "2px",
      }}
    >
      <div
        className="
          flex
          h-[14px]
          w-[14px]
          items-center
          justify-center
          border
          border-gray-300
          bg-[#f8f8f8]
        "
      >
        <span
          className="h-2 w-2 rounded-full"
          style={{
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
};

export default function AllRestaurants() {
  const navigate = useNavigate();

  const { getCatalogueStatus } = useCatalogueStatus();
  const { getRestaurantDetails } = useRestaurantDetails();

  const [checkingRestaurantId, setCheckingRestaurantId] = useState("");

  const locationRequestedRef = useRef(false);

  const lastRequestKeyRef = useRef("");

  const filterScrollTimeoutRef = useRef(null);

  const [isFilterScrolling, setIsFilterScrolling] = useState(false);

  const [categoryRequestLoading, setCategoryRequestLoading] = useState(false);

  const categoryShimmerStartRef = useRef(0);

  const handleFilterScroll = useCallback(() => {
    setIsFilterScrolling(true);

    if (filterScrollTimeoutRef.current) {
      clearTimeout(filterScrollTimeoutRef.current);
    }

    filterScrollTimeoutRef.current = setTimeout(() => {
      setIsFilterScrolling(false);
    }, 700);
  }, []);

  useEffect(() => {
    return () => {
      if (filterScrollTimeoutRef.current) {
        clearTimeout(filterScrollTimeoutRef.current);
      }
    };
  }, []);

  const {
    selectedCategoryIds = [],
    selectedCategoryName,
    selectedOrderType,
    selectedPlatform,
    selectRestaurant,
    selectedDeliveryLocation,
  } = useGlobalContext();
const {
  restaurants,
  getRestaurants,
  restaurantLoading,
  loadingMoreRestaurants,
  restaurantError,
  hasMoreRestaurants,
} = useRestaurant();

  const [activeImageIndexes, setActiveImageIndexes] = useState({});

  const [imageTransitionEnabled, setImageTransitionEnabled] = useState({});
  const [loadedRestaurantImages, setLoadedRestaurantImages] = useState({});

  const [activeFilter, setActiveFilter] = useState("all");

  const [sortBy, setSortBy] = useState("Sort By");
  const [sortOpen, setSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  const sortOptions = [
    { value: "Sort By", label: "Sort By" },
    { value: "rating-high", label: "Rating" },
    { value: "price-high", label: "Price: High to Low" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "distance", label: "Distance" },
    { value: "name", label: "Name" },
  ];

  const selectedSortLabel =
    sortOptions.find((option) => option.value === sortBy)?.label || "Sort By";

  const loadMoreRef = useRef(null);
  const loadMoreLockRef = useRef(false);

  const [locationLoading, setLocationLoading] = useState(true);

  const [locationError, setLocationError] = useState("");

  const [coordinates, setCoordinates] = useState({
    latitude: null,
    longitude: null,
  });

  const normalizedCategoryIds = useMemo(() => {
    if (!Array.isArray(selectedCategoryIds)) {
      return [];
    }

    return selectedCategoryIds
      .map((categoryId) => Number(categoryId))
      .filter((categoryId) => Number.isFinite(categoryId));
  }, [selectedCategoryIds]);

  const categoryKey = normalizedCategoryIds.join(",");

  const activeFilterData = useMemo(() => {
    return filters.find((filter) => filter.id === activeFilter) || filters[0];
  }, [activeFilter]);

  // =====================================================
  // LOCATION
  // =====================================================

  const requestCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationLoading(false);

      setLocationError("Location is not supported by this browser.");

      return;
    }

    setLocationLoading(true);
    setLocationError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude;

        const longitude = position.coords.longitude;

        setCoordinates({
          latitude,
          longitude,
        });

        setLocationLoading(false);
      },

      (error) => {
        console.error("Location error:", error);

        setLocationError(getLocationErrorMessage(error));

        setLocationLoading(false);
      },

      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000,
      },
    );
  }, []);

  useEffect(() => {
    if (locationRequestedRef.current) {
      return;
    }

    locationRequestedRef.current = true;

    requestCurrentLocation();
  }, [requestCurrentLocation]);

  // =====================================================
  // GET RESTAURANTS
  // =====================================================

  const fetchInitialRestaurants = useCallback(
    async (force = false) => {
      if (coordinates.latitude === null || coordinates.longitude === null) {
        return;
      }

      const requestKey = [
        coordinates.latitude,
        coordinates.longitude,
        categoryKey,
        activeFilterData.servingType,
        selectedCategoryName || "",
      ].join("|");

      if (!force && lastRequestKeyRef.current === requestKey) {
        return;
      }

      lastRequestKeyRef.current = requestKey;

      loadMoreLockRef.current = false;
      setLocationError("");

      // =====================================================
      // NEW CATEGORY / RESTAURANT DATA
      // RESET OLD IMAGE LOAD STATE
      // =====================================================

      setLoadedRestaurantImages({});
      setActiveImageIndexes({});

      // =====================================================
      // CATEGORY / RESTAURANT CHANGE SHIMMER START
      // =====================================================

      setCategoryRequestLoading(true);
      categoryShimmerStartRef.current = Date.now();

      // React ko shimmer render karne ka ek frame do
      await new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
      });

      try {
     await getRestaurants({
       categoryIds: normalizedCategoryIds,
       customerLatitude: coordinates.latitude,
       customerLongitude: coordinates.longitude,

       limit: API_LIMIT,
       offset: 0,
       range: ALL_RESTAURANT_RANGE_KM,

       servingType: activeFilterData.servingType,

       bestOf:
         String(selectedCategoryName || "")
           .trim()
           .toLowerCase() === "all"
           ? ""
           : String(selectedCategoryName || "").trim(),

       append: false,

       forceRefresh: force,
     });
      } finally {
        const elapsed = Date.now() - categoryShimmerStartRef.current;

        // Shimmer minimum 600ms visible rahe
        const remainingTime = Math.max(0, 600 - elapsed);

        window.setTimeout(() => {
          setCategoryRequestLoading(false);
        }, remainingTime);
      }
    },
    [
      coordinates.latitude,
      coordinates.longitude,
      categoryKey,
      activeFilterData.servingType,
      normalizedCategoryIds,
      selectedCategoryName,
      getRestaurants,
    ],
  );

  useEffect(() => {
    fetchInitialRestaurants();
  }, [fetchInitialRestaurants]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

const restaurantsWithDistance = useMemo(() => {
  return restaurants.map((restaurant) => {
    const rawRestaurant = restaurant?.raw || restaurant;

    // =====================================================
    // STARTING PRICE
    // Sirf restaurant API se aane wali price use hogi
    // =====================================================

    const apiStartingPrice = Number(
      restaurant?.startingPrice ??
        restaurant?.starting_price ??
        rawRestaurant?.startingPrice ??
        rawRestaurant?.starting_price,
    );

    // =====================================================
    // DISTANCE
    // =====================================================

    const apiDistance = Number(restaurant?.distance ?? rawRestaurant?.distance);

    // =====================================================
    // ESTIMATED TIME
    // =====================================================

    const apiEstimatedTime = Number(
      restaurant?.estimatedTime ??
        restaurant?.estimated_time ??
        restaurant?.deliveryTime ??
        rawRestaurant?.estimated_time ??
        rawRestaurant?.estimatedTime,
    );

    return {
      ...restaurant,

      startingPrice:
        Number.isFinite(apiStartingPrice) && apiStartingPrice > 0
          ? apiStartingPrice
          : null,

      distance:
        Number.isFinite(apiDistance) && apiDistance >= 0 ? apiDistance : null,

      estimatedTime:
        Number.isFinite(apiEstimatedTime) && apiEstimatedTime > 0
          ? apiEstimatedTime
          : null,
    };
  });
}, [restaurants]);

  // 👇 ISKE BAAD TUMHARA EXISTING FILTER CODE

  const filteredRestaurants = useMemo(() => {
    let result = [...restaurantsWithDistance];

    if (activeFilter === "top-rated") {
      result = result.filter(
        (restaurant) => Number(restaurant.rating || 0) >= 4.5,
      );
    }
    if (activeFilter === "under-30") {
      result = result.filter((restaurant) => {
        const estimatedTime = Number(restaurant.estimatedTime);

        return (
          Number.isFinite(estimatedTime) &&
          estimatedTime > 0 &&
          estimatedTime <= 30
        );
      });
    }

    if (activeFilter === "under-45") {
      result = result.filter((restaurant) => {
        const estimatedTime = Number(restaurant.estimatedTime);

        return (
          Number.isFinite(estimatedTime) &&
          estimatedTime > 0 &&
          estimatedTime <= 45
        );
      });
    }

    if (activeFilter === "under-60") {
      result = result.filter((restaurant) => {
        const estimatedTime = Number(restaurant.estimatedTime);

        return (
          Number.isFinite(estimatedTime) &&
          estimatedTime > 0 &&
          estimatedTime <= 60
        );
      });
    }

    if (activeFilter === "below-50") {
      result = result.filter((restaurant) => {
        const price = Number(restaurant.startingPrice);

        return Number.isFinite(price) && price > 0 && price < 50;
      });
    }

    if (activeFilter === "below-100") {
      result = result.filter((restaurant) => {
        const price = Number(restaurant.startingPrice);

        return Number.isFinite(price) && price > 0 && price < 100;
      });
    }

    if (activeFilter === "below-150") {
      result = result.filter((restaurant) => {
        const price = Number(restaurant.startingPrice);

        return Number.isFinite(price) && price > 0 && price < 150;
      });
    }

    if (activeFilter === "150-300") {
      result = result.filter((restaurant) => {
        const price = Number(restaurant.startingPrice);

        return Number.isFinite(price) && price >= 150 && price <= 300;
      });
    }

    if (activeFilter === "above-300") {
      result = result.filter((restaurant) => {
        const price = Number(restaurant.startingPrice);

        return Number.isFinite(price) && price > 300;
      });
    }

    if (sortBy === "rating-high") {
      result.sort(
        (first, second) =>
          Number(second.rating || 0) - Number(first.rating || 0),
      );
    }

    if (sortBy === "price-low") {
      result.sort(
        (first, second) =>
          (first.startingPrice ?? Infinity) -
          (second.startingPrice ?? Infinity),
      );
    }

    if (sortBy === "price-high") {
      result.sort(
        (first, second) =>
          (second.startingPrice ?? -1) - (first.startingPrice ?? -1),
      );
    }

    if (sortBy === "distance") {
      result.sort(
        (first, second) =>
          (first.distance ?? Infinity) - (second.distance ?? Infinity),
      );
    }

    if (sortBy === "name") {
      result.sort((first, second) =>
        String(first.name || "").localeCompare(String(second.name || "")),
      );
    }

    return result;
  }, [restaurantsWithDistance, activeFilter, sortBy]);

  // =====================================================
  // INFINITE SCROLL RESTAURANTS
  // =====================================================

  const visibleRestaurants = filteredRestaurants;

// =====================================================
// PAGE LOADING / ERROR STATE
// Infinite scroll observer se pehle define hona zaroori hai
// =====================================================

const showLoading =
  locationLoading || restaurantLoading || categoryRequestLoading;

const visibleError =
  locationError || (restaurants.length === 0 ? restaurantError : "");

// =====================================================
// INFINITE SCROLL RESTAURANTS
// =====================================================


 const loadMoreRestaurants = useThrottledCallback(async () => {
   if (
     loadMoreLockRef.current ||
     loadingMoreRestaurants ||
     restaurantLoading ||
     !hasMoreRestaurants ||
     coordinates.latitude === null ||
     coordinates.longitude === null
   ) {
     return;
   }

   loadMoreLockRef.current = true;

   const currentOffset = restaurants.length;

   console.log("🔥 INFINITE SCROLL API CALL", {
     limit: API_LIMIT,
     offset: currentOffset,
   });

   try {
     await getRestaurants({
       categoryIds: normalizedCategoryIds,
       customerLatitude: coordinates.latitude,
       customerLongitude: coordinates.longitude,
       limit: API_LIMIT,
       offset: currentOffset,
       range: ALL_RESTAURANT_RANGE_KM,
       servingType: activeFilterData.servingType,

       bestOf:
         String(selectedCategoryName || "")
           .trim()
           .toLowerCase() === "all"
           ? ""
           : String(selectedCategoryName || "").trim(),

       append: true,
       forceRefresh: false,
     });
   } catch (error) {
     console.error("Load more restaurants error:", error);
   } finally {
     loadMoreLockRef.current = false;
   }
 }, 1200);

  // =====================================================
  // OBSERVE BOTTOM OF RESTAURANT LIST
  // =====================================================

 useEffect(() => {
   const target = loadMoreRef.current;

   if (!target || !hasMoreRestaurants || showLoading) {
     return;
   }

   const observer = new IntersectionObserver(
     (entries) => {
       const entry = entries[0];

       if (!entry?.isIntersecting) {
         return;
       }

       console.log("👀 LOAD MORE TRIGGER VISIBLE");

       loadMoreRestaurants();
     },
     {
       root: null,
       rootMargin: "250px 0px",
       threshold: 0.01,
     },
   );

   observer.observe(target);

   return () => {
     observer.disconnect();
   };
 }, [hasMoreRestaurants, showLoading, loadMoreRestaurants]);

const handleFilterChange = (filterId) => {
  if (activeFilter === filterId) {
    return;
  }

  loadMoreLockRef.current = false;
  setActiveFilter(filterId);
};

  const handleRetry = () => {
    if (coordinates.latitude === null || coordinates.longitude === null) {
      setLocationError("Please select a delivery location.");

      return;
    }

    lastRequestKeyRef.current = "";

    fetchInitialRestaurants(true);
  };

  useEffect(() => {
    const timers = [];
    let cancelled = false;

    // =====================================================
    // RANDOM INITIAL IMAGE
    // =====================================================

    setActiveImageIndexes((previous) => {
      const next = { ...previous };

      visibleRestaurants.forEach((restaurant, index) => {
        const restaurantId = String(
          restaurant.id ||
            getRestaurantUserId(restaurant) ||
            `restaurant-${index}`,
        );

        const images = Array.isArray(restaurant.image)
          ? restaurant.image
          : String(restaurant.image || "")
              .split(",")
              .map((url) => url.trim())
              .filter(Boolean);

        if (images.length > 1 && previous[restaurantId] === undefined) {
          next[restaurantId] = Math.floor(Math.random() * images.length);
        }
      });

      return next;
    });

    setImageTransitionEnabled((previous) => {
      const next = { ...previous };

      visibleRestaurants.forEach((restaurant, index) => {
        const restaurantId = String(
          restaurant.id ||
            getRestaurantUserId(restaurant) ||
            `restaurant-${index}`,
        );

        next[restaurantId] = true;
      });

      return next;
    });

    // =====================================================
    // AUTO SLIDER
    // =====================================================

    visibleRestaurants.forEach((restaurant, index) => {
      const restaurantId = String(
        restaurant.id ||
          getRestaurantUserId(restaurant) ||
          `restaurant-${index}`,
      );

      const images = Array.isArray(restaurant.image)
        ? restaurant.image
        : String(restaurant.image || "")
            .split(",")
            .map((url) => url.trim())
            .filter(Boolean);

      if (images.length <= 1) {
        return;
      }

      const scheduleNextImage = () => {
        if (cancelled) {
          return;
        }

        // Har restaurant ka timing different
        const randomDelay = 3000 + Math.floor(Math.random() * 3000);

        const timer = setTimeout(() => {
          if (cancelled) {
            return;
          }

          setImageTransitionEnabled((previous) => ({
            ...previous,
            [restaurantId]: true,
          }));

          setActiveImageIndexes((previous) => {
            const currentIndex = previous[restaurantId] ?? 0;

            const nextIndex = currentIndex + 1;

            // =================================================
            // LAST IMAGE → CLONED FIRST IMAGE
            // =================================================

            if (nextIndex === images.length) {
              const resetTimer = setTimeout(() => {
                if (cancelled) {
                  return;
                }

                // Transition temporarily disable
                setImageTransitionEnabled((previousTransition) => ({
                  ...previousTransition,
                  [restaurantId]: false,
                }));

                // Invisible reset back to first image
                setActiveImageIndexes((previousIndex) => ({
                  ...previousIndex,
                  [restaurantId]: 0,
                }));

                // Next frame me transition wapas enable
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    if (cancelled) {
                      return;
                    }

                    setImageTransitionEnabled((previousTransition) => ({
                      ...previousTransition,
                      [restaurantId]: true,
                    }));
                  });
                });
              }, 800);

              timers.push(resetTimer);
            }

            return {
              ...previous,
              [restaurantId]: nextIndex,
            };
          });

          scheduleNextImage();
        }, randomDelay);

        timers.push(timer);
      };

      scheduleNextImage();
    });

    return () => {
      cancelled = true;

      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, [visibleRestaurants]);

  // =====================================================
  // RESTAURANT CLICK
  // =====================================================

const handleRestaurantClick = async (restaurant) => {
  const restaurantUserId = getRestaurantUserId(restaurant);

  if (!restaurantUserId) {
    console.error("Restaurant userid missing:", restaurant);
    return;
  }

  if (checkingRestaurantId === restaurantUserId) {
    return;
  }
const currentOrderType = "home_delivery";

  const currentPlatform = String(selectedPlatform || "").trim();

  const selectedItemName = String(selectedCategoryName || "").trim();

 const selectedRestaurantData = {
   userId: restaurantUserId,

   name: restaurant?.name || restaurant?.restaurantName || "Restaurant",

   image: restaurant?.image || restaurant?.restaurantImage || "",

   address: String(restaurant?.address || restaurant?.street || "").trim(),

   phone: String(
     restaurant?.phone || restaurant?.restaurant_phone || "",
   ).trim(),

   city: String(restaurant?.city || "").trim(),

   state: String(restaurant?.state || "").trim(),

   latitude: restaurant?.latitude ?? "",

   longitude: restaurant?.longitude ?? restaurant?.lontitude ?? "",

   servingType: getRestaurantServingType(restaurant),

   startingPrice: Number.isFinite(
     Number(restaurant?.startingPrice ?? restaurant?.starting_price),
   )
     ? Number(restaurant?.startingPrice ?? restaurant?.starting_price)
     : null,

   customerLatitude: coordinates.latitude,
   customerLongitude: coordinates.longitude,

   selectedItemName,
 };

  try {
    setCheckingRestaurantId(restaurantUserId);

    // =====================================================
    // BOTH APIs CALL ON RESTAURANT CARD CLICK
    // =====================================================

    const [statusResult, detailsResult] = await Promise.all([
      getCatalogueStatus({
        userid: restaurantUserId,
      }),

      getRestaurantDetails({
        user_id: restaurantUserId,
      }),
    ]);

    console.log("CATALOGUE STATUS RESULT:", statusResult);
    console.log("RESTAURANT DETAILS RESULT:", detailsResult);

    // Catalogue status existing flow ke liye required hai
    if (!statusResult?.success) {
      console.error(
        statusResult?.message || "Unable to check catalogue status.",
      );

      return;
    }

    // Details API fail hone par bhi old menu flow block nahi hoga
    const apiRestaurantDetails =
      detailsResult?.success && detailsResult?.data ? detailsResult.data : null;

    if (!detailsResult?.success) {
      console.error(
        detailsResult?.message || "Unable to fetch restaurant details.",
      );
    }

    // =====================================================
    // KEEP EXISTING SELECTED RESTAURANT FLOW
    // =====================================================

    selectRestaurant(selectedRestaurantData);

    // =====================================================
    // OPEN EXISTING RESTAURANT PAGE
    // Restaurant details API response bhi pass karo
    // =====================================================

    navigate(`/restaurant-details/${restaurantUserId}`, {
      state: {
        restaurant: selectedRestaurantData,

        orderType: currentOrderType,

        platform: currentPlatform,

        selectedItemName,

        catalogueStatus: statusResult.catalogueStatus,

        // NEW
        restaurantDetails: apiRestaurantDetails,
      },
    });
  } catch (error) {
    console.error("Restaurant click API error:", error);
  } finally {
    setCheckingRestaurantId("");
  }
};

  const handleRestaurantKeyDown = (event, restaurant) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();

    handleRestaurantClick(restaurant);
  };
  return (
    <>
      <ShimmerStyles />
      <section
        id="all-restaurants"
        className="w-full scroll-mt-24 bg-blue-50 py-5"
      >
        <div className="mx-auto w-full max-w-[1330px] px-4 md:px-8">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.5px] text-[#1f2937] sm:text-[31px] md:text-[36px]">
                All Restaurants
              </h2>

              <div className="mt-1 flex items-center gap-2 text-[13px] text-[#91a2ba] md:text-[17px]">
                {showLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />

                    <span>Loading all restaurants...</span>
                  </>
                ) : (
                  <>
                    <MapPin
                      strokeWidth={2}
                      className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]"
                    />

                    <span className="whitespace-nowrap">
                      Restaurants available near your location
                    </span>
                  </>
                )}
              </div>
            </div>

            <div
              ref={sortDropdownRef}
              className="relative z-50 w-[112px] flex-shrink-0 sm:w-[160px]"
            >
              {/* SELECT BUTTON */}
              <button
                type="button"
                onClick={() => setSortOpen((previous) => !previous)}
                className="
      flex
      h-[30px]
      w-full
      cursor-pointer
      items-center
      justify-between
      rounded-[9px]
      border
      border-[#dfe4ea]
      bg-white
      px-3
      text-left
      text-[12px]
      text-[#555]
      outline-none
      transition
      hover:border-[#c4ccd6]
      focus:border-[#9eabc0]

      sm:h-[46px]
      sm:px-4
      sm:text-[12px]
    "
              >
                <span className="truncate">{selectedSortLabel}</span>

                <ChevronDown
                  size={16}
                  strokeWidth={2}
                  className={`
        ml-2
        shrink-0
        text-[#91a2ba]
        transition-transform
        duration-200
        ${sortOpen ? "rotate-180" : ""}
      `}
                />
              </button>

              {/* DROPDOWN */}
              {sortOpen && (
                <div
                  className="
        absolute
        left-0
        top-full
        z-[100]
        mt-[2px]
        w-full
        overflow-hidden
        rounded-[10px]
        border
        border-[#dfe4ea]
        bg-white
        py-1
        shadow-[0_8px_24px_rgba(15,23,42,0.12)]
      "
                >
                  {sortOptions.map((option) => {
                    const isSelected = sortBy === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setSortBy(option.value);
                          setSortOpen(false);
                        }}
                        className={`
              block
              w-full
              px-4
              py-2
              text-left
              text-[12px]
              transition

              sm:text-[14px]

              ${
                isSelected
                  ? "bg-[#2B4578] text-white"
                  : "bg-white text-[#555] hover:bg-[#f5f7fa]"
              }
            `}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Filters - Horizontal Scroll */}
          <div className="mt-6 w-full min-w-0 max-w-full overflow-hidden">
            <div
              onScroll={handleFilterScroll}
              onWheel={(event) => {
                const element = event.currentTarget;

                const canScroll = element.scrollWidth > element.clientWidth;

                if (!canScroll) return;

                const scrollingRight = event.deltaY > 0;
                const scrollingLeft = event.deltaY < 0;

                const isAtStart = element.scrollLeft <= 0;

                const isAtEnd =
                  Math.ceil(element.scrollLeft + element.clientWidth) >=
                  element.scrollWidth;

                // Filter ke end/start par normal page scrolling allow karo
                if (
                  (scrollingLeft && isAtStart) ||
                  (scrollingRight && isAtEnd)
                ) {
                  return;
                }

                event.preventDefault();

                handleFilterScroll();

                element.scrollBy({
                  left: event.deltaY,
                  behavior: "auto",
                });
              }}
              className={`
    filter-horizontal-scroll
    ${isFilterScrolling ? "filter-scroll-active" : ""}
    flex
    w-full
    max-w-full
    gap-3
    overflow-x-auto
    overflow-y-hidden
    pb-3
    touch-pan-x
    overscroll-x-contain
  `}
            >
              {filters.map((filter) => {
                const isActive = activeFilter === filter.id;

                return (
                  <button
                    key={filter.id}
                    type="button"
                    disabled={showLoading}
                    onClick={() => handleFilterChange(filter.id)}
                    className={`
    shrink-0
    whitespace-nowrap
    rounded-full
    border
    font-medium
    transition

    h-[36px]
    px-3
    text-[12px]

    min-[380px]:h-[38px]
    min-[380px]:px-4
    min-[380px]:text-[13px]

    sm:h-[40px]
    sm:px-5
    sm:text-[14px]

    md:h-[42px]
    md:px-5
    md:text-[15px]

    lg:h-[46px]
    lg:px-5
    lg:text-[15px]

    disabled:cursor-not-allowed
    disabled:opacity-60

    ${
      isActive
        ? "border-[#11182a] bg-[#11182a] text-white"
        : "border-[#dce2e8] bg-white text-[#555] hover:border-[#aeb8c7]"
    }
  `}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* =====================================================
    INITIAL RESTAURANT LOADING
===================================================== */}

          {showLoading && (
            <div className="flex min-h-[350px] w-full items-center justify-center">
              <LoadingScreen text="Loading restaurants..." />
            </div>
          )}

          {/* Error */}
          {!showLoading && visibleRestaurants.length === 0 && visibleError && (
            <div className="mt-8 flex min-h-[240px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#d9e0e8] bg-white px-5 text-center">
              <MapPin size={30} className="text-[#ff6534]" />

              <p className="mt-4 text-[16px] font-semibold text-[#333]">
                Restaurants could not be loaded
              </p>

              <p className="mt-1 max-w-md text-[14px] leading-6 text-[#8799b4]">
                {visibleError}
              </p>

              <button
                type="button"
                onClick={handleRetry}
                className="mt-5 flex h-11 items-center justify-center gap-2 rounded-[9px] bg-[#11182a] px-6 text-[14px] font-medium text-white"
              >
                <RefreshCw size={17} />
                Try again
              </button>
            </div>
          )}

          {/* Restaurant grid */}
          {!showLoading && visibleRestaurants.length > 0 && (
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleRestaurants.map((restaurant, index) => {
                const userId = getRestaurantUserId(restaurant);

                const restaurantId = String(
                  restaurant.id || userId || `restaurant-${index}`,
                );

                const restaurantName =
                  restaurant.name || restaurant.restaurant_name || "Restaurant";

                const servingType = getRestaurantServingType(restaurant);

                const servingLabel = getServingLabel(servingType);

                const distanceText =
                  restaurant.distance !== null &&
                  restaurant.distance !== undefined &&
                  Number.isFinite(Number(restaurant.distance))
                    ? `${Number(restaurant.distance).toFixed(1)} km`
                    : "";

                const estimatedTimeText =
                  restaurant.estimatedTime !== null &&
                  restaurant.estimatedTime !== undefined &&
                  Number.isFinite(Number(restaurant.estimatedTime))
                    ? `${Number(restaurant.estimatedTime)}-${Number(restaurant.estimatedTime) + 10} min`
                    : "";

                const restaurantImages = Array.isArray(restaurant.image)
                  ? restaurant.image
                  : restaurant.image
                    ? String(restaurant.image)
                        .split(",")
                        .map((url) => url.trim())
                        .filter(Boolean)
                    : restaurant.restaurant_image
                      ? String(restaurant.restaurant_image)
                          .split(",")
                          .map((url) => url.trim())
                          .filter(Boolean)
                      : [];

                const activeImageIndex = activeImageIndexes[restaurantId] || 0;

                const imageUrl =
                  restaurantImages[activeImageIndex] ||
                  restaurantImages[0] ||
                  NoImg;

                return (
                  <article
                    key={restaurantId}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRestaurantClick(restaurant)}
                    onKeyDown={(event) =>
                      handleRestaurantKeyDown(event, restaurant)
                    }
                    className="group min-w-0 cursor-pointer overflow-hidden rounded-[16px] border border-[#dfe4ea] bg-white outline-none transition duration-300 hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(15,23,42,0.1)] focus-visible:ring-2 focus-visible:ring-[#2B4578]"
                  >
                    {/* Image */}
                    <div className="relative h-[155px] w-full overflow-hidden bg-[#f8fafc] sm:h-[175px]">
                      {/* =====================================================
    RESTAURANT IMAGE SLIDER
===================================================== */}
                      {restaurantImages.length > 0 ? (
                        <div
                          className="
      flex
      h-full
      w-full
      will-change-transform
    "
                          style={{
                            transform: `translate3d(-${activeImageIndex * 100}%, 0, 0)`,

                            transition:
                              imageTransitionEnabled[restaurantId] === false
                                ? "none"
                                : "transform 800ms cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          {[
                            ...restaurantImages,

                            // First image duplicate for infinite slider
                            restaurantImages[0],
                          ].map((image, imageIndex) => {
                            const imageKey = `${restaurantId}-${imageIndex}-${image}`;

                            const isImageLoaded =
                              loadedRestaurantImages[imageKey] === true;

                            return (
                              <div
                                key={`${restaurantId}-slider-${imageIndex}`}
                                className="
            relative
            h-full
            w-full
            shrink-0
            overflow-hidden
            bg-[#f8fafc]
          "
                              >
                                {/* =================================================
              SHIMMER
              Jab tak image load nahi hoti tab tak chalega
          ================================================= */}

                                {!isImageLoaded && (
                                  <div
                                    className="
                chaafo-shimmer
                absolute
                inset-0
                z-10
                h-full
                w-full
              "
                                  />
                                )}

                                {/* =================================================
              IMAGE
          ================================================= */}

                                <img
                                  src={image || NoImg}
                                  alt={`${restaurantName} ${imageIndex + 1}`}
                                  loading="lazy"
                                  draggable={false}
                                  onLoad={() => {
                                    setLoadedRestaurantImages((previous) => ({
                                      ...previous,
                                      [imageKey]: true,
                                    }));
                                  }}
                                  onError={(event) => {
                                    // Image load nahi hui → shimmer visible hi rehne do
                                    setLoadedRestaurantImages((previous) => ({
                                      ...previous,
                                      [imageKey]: false,
                                    }));

                                    event.currentTarget.style.display = "none";
                                  }}
                                  className={`
  block
  h-full
  w-full
  object-cover

  transition-opacity
  duration-300

  ${isImageLoaded ? "opacity-100" : "opacity-0"}
`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <img
                          src={NoImg}
                          alt={restaurantName}
                          className="
    block
    h-full
    w-full
    object-cover
  "
                        />
                      )}
                      {Number(restaurant.rating) > 0 && (
                        <div
                          className="
    absolute
    right-3
    top-3
    z-20

    flex
    items-center
    gap-1

    rounded-full
    bg-[#16a34a]

    px-2.5
    py-1

    text-[12px]
    font-semibold
    text-white

    shadow-md

    transition-transform
    duration-300

    group-hover:translate-y-1
  "
                        >
                          <Star
                            size={13}
                            strokeWidth={0}
                            fill="currentColor"
                            className="shrink-0"
                          />

                          <span className="shrink-0 tabular-nums leading-none">
                            {Number(restaurant.rating).toFixed(1)}
                          </span>
                        </div>
                      )}
                      {/* Image slider dots */}
                      {restaurantImages.length > 1 && (
                        <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
                          {restaurantImages.map((_, imageIndex) => (
                            <button
                              key={imageIndex}
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();

                                setActiveImageIndexes((previous) => ({
                                  ...previous,
                                  [restaurantId]: imageIndex,
                                }));
                              }}
                              className={`h-1.5 rounded-full transition-all ${
                                activeImageIndex === imageIndex
                                  ? "w-4 bg-white"
                                  : "w-1.5 bg-white/60"
                              }`}
                              aria-label={`Show image ${imageIndex + 1}`}
                            />
                          ))}
                        </div>
                      )}
                      {/* Veg / Non Veg icon */}
                      {(servingType === "V" || servingType === "N") && (
                        <div className="absolute left-3 top-3 z-10">
                          <FoodTypeIcon type={servingType} />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="px-[12px] pb-[14px] pt-[11px]">
                      <h3
                        title={restaurantName}
                        className="truncate text-[17px] font-medium leading-6 text-[#505050] transition group-hover:text-[#2B4578]"
                      >
                        {restaurantName}
                      </h3>

                      {/* Price + Distance + Time */}
                      <div className="mt-2.5 flex items-center justify-between gap-3">
                        <div
                          className="
    flex
    shrink-0
    items-center
    gap-1.5
    text-[10px]
    font-medium
    text-[#68768c]

    min-[380px]:gap-2
    min-[380px]:text-[11px]

    sm:gap-2.5
    sm:text-[12px]
  "
                        >
                          {distanceText && (
                            <div className="flex shrink-0 items-center gap-1">
                              <MapPin
                                strokeWidth={2}
                                className="
        h-3
        w-3
        shrink-0
        text-[#68768c]

        min-[380px]:h-[13px]
        min-[380px]:w-[13px]

        sm:h-3.5
        sm:w-3.5
      "
                              />

                              <span className="shrink-0 whitespace-nowrap">
                                {distanceText}
                              </span>
                            </div>
                          )}

                          {estimatedTimeText && (
                            <div className="flex items-center gap-1">
                              <Clock3
                                strokeWidth={2}
                                className="
    h-3
    w-3
    shrink-0

    min-[380px]:h-[13px]
    min-[380px]:w-[13px]

    sm:h-3.5
    sm:w-3.5
  "
                              />

                              <span className="whitespace-nowrap">
                                {estimatedTimeText}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="min-w-0">
                          {restaurant.startingPrice !== null &&
                            restaurant.startingPrice !== undefined &&
                            Number(restaurant.startingPrice) > 0 && (
                              <div className="flex items-center gap-0.5 text-[13px] font-medium text-[#8799b4]">
                                <IndianRupee size={14} strokeWidth={1.8} />

                                <span className="truncate">
                                  {restaurant.startingPrice} onwards
                                </span>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* Empty */}
          {!showLoading && !visibleError && visibleRestaurants.length === 0 && (
            <div className="mt-10 flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-[#d9e0e8] bg-white">
              <p className="text-[16px] text-[#8799b4]">
                No restaurants found.
              </p>
            </div>
          )}

          {/* =====================================================
    INFINITE SCROLL LOADING
===================================================== */}

          {!showLoading &&
            visibleRestaurants.length > 0 &&
            loadingMoreRestaurants && (
              <div className="flex min-h-[150px] w-full items-center justify-center py-5">
                <LoadingScreen text="Loading more restaurants..." />
              </div>
            )}
          {/* =====================================================
    INFINITE SCROLL TRIGGER
===================================================== */}

          {!showLoading &&
            !visibleError &&
            visibleRestaurants.length > 0 &&
            hasMoreRestaurants && (
              <div
                ref={loadMoreRef}
                className="h-[2px] w-full"
                aria-hidden="true"
              />
            )}
        </div>
      </section>
    </>
  );
}
